import React, { useState, useEffect, useMemo } from 'react';
import DataTable from '../components/common/DataTable';
import SearchBar from '../components/common/SearchBar';
import PageHeader from '../components/common/PageHeader';
import Pagination from '../components/common/Pagination';
import DeleteConfirmModal from '../components/common/DeleteConfirmModal';
import useDebouncedSearch from '../hooks/useDebouncedSearch';
import { Hash, Eye, Calendar } from 'lucide-react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { InquiryService } from '../services/inquiry.service';

const COLUMNS = [
  { header: 'ID', accessor: 'id' },
  { header: 'Full Name', accessor: 'name' },
  { header: 'Email', accessor: 'email' },
  { header: 'Phone', accessor: 'mobile_number' },
  // { header: 'Date', accessor: 'date' },
  { header: 'Message', accessor: 'message' },
  // { header: 'Status', accessor: 'status' }
];

export default function Inquiries() {
  const navigate = useNavigate();
  const [dataList, setDataList] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  // Search, Filter & Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const { searchQuery, setSearchQuery, debouncedQuery } = useDebouncedSearch(() => setCurrentPage(1));
  const itemsPerPage = 5;

  // Fetch from API
  const fetchData = async () => {
    setIsLoading(true);
    try {
      const res = await InquiryService.getAllInquiries();
      const list = Array.isArray(res) ? res : (res?.data || []);
      const formattedList = list.map(item => ({
        ...item,
        id: item._id || item.id,
        date: item.date || (item.createdAt ? new Date(item.createdAt).toISOString().split('T')[0] : ''),
        status: item.status || 'Pending'
      }));
      setDataList(formattedList);
    } catch (error) {
      toast.error('Failed to load inquiries');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);



  // Combined Filtering Logic
  const filteredData = useMemo(() => {
    let result = dataList;

    if (debouncedQuery) {
      result = result.filter(item =>
        Object.values(item).some(val =>
          String(val).toLowerCase().includes(debouncedQuery.toLowerCase())
        )
      );
    }

    return result;
  }, [dataList, debouncedQuery]);

  // KPI Calculations
  const kpis = useMemo(() => {
    const total = dataList.length;
    const pending = dataList.filter(d => d.status !== 'Resolved').length;
    const resolved = dataList.filter(d => d.status === 'Resolved').length;
    return [
      { label: "Total Inquiries", value: total, icon: <Hash size={20} />, color: 'blue' },
      { label: "Pending Review", value: pending, icon: <Eye size={20} />, color: 'orange' },
      { label: "Resolved", value: resolved, icon: <Hash size={20} />, color: 'green' }
    ];
  }, [dataList]);

  // Pagination Logic
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const paginatedData = filteredData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handleDeleteClick = (row) => {
    setDeleteTarget(row);
    setIsDeleteModalOpen(true);
  };

  const handleViewClick = (row) => {
    navigate(`/inqueries/${row.id || row._id}`);
  };

  const handleDeleteExecute = async () => {
    if (!deleteTarget) return;
    try {
      const targetId = deleteTarget._id || deleteTarget.id;
      await InquiryService.deleteInquiry(targetId);
      setDataList(prev => prev.filter(item => (item._id || item.id) !== targetId));
      toast.success('Inquiry deleted successfully!');
    } catch (error) {
      toast.error('Failed to delete inquiry');
    } finally {
      setIsDeleteModalOpen(false);
      setDeleteTarget(null);
    }
  };

  return (
    <div className="page-content">
      <PageHeader
        title="Website Inqueries"
        subtitle="Manage and respond to website user inquiries efficiently"
        kpis={kpis}
      />

      <div className="table-card-container" style={{ transition: 'all 0.3s ease' }}>
        <div className="table-actions-area">
          <div className="table-actions-left">
            <SearchBar
              placeholder="Search inquiries..."
              value={searchQuery}
              onChange={setSearchQuery}
            />
          </div>

          <div className="table-actions-right">
            <div className="filter-select-wrapper">
              <Calendar size={16} className="filter-icon" />
              <input
                type="date"
                className="table-filter-select"
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Filter by date"
              />
            </div>
          </div>
        </div>

        <div className="table-responsive-wrapper">
          <DataTable
            columns={COLUMNS}
            data={paginatedData}
            onDelete={handleDeleteClick}
            onView={handleViewClick}
            hideEdit={true}
            loading={isLoading}
          />
        </div>
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalEntries={filteredData.length}
          itemsPerPage={itemsPerPage}
          onPageChange={setCurrentPage}
        />
      </div>

      <DeleteConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => { setIsDeleteModalOpen(false); setDeleteTarget(null); }}
        onConfirm={handleDeleteExecute}
        title="Delete Inquiry"
        message="Are you sure you want to delete this inquiry from this user?"
        itemName={deleteTarget ? `${deleteTarget.name} (${deleteTarget.email})` : ''}
      />
    </div>
  );
}
